'use client';

import { useQuery } from '@tanstack/react-query';
import { getInteractionAggregates } from '@/api/generated/interactions/interactions';
import { getChartData } from '@/api/generated/reports/reports';
import { TimeRangeKind } from '@/api/generated/model';
import { QUERY_KEY_FETCH_ANALYTICS_CHART_DATA, QUERY_KEY_INTERACTION_AGGREGATES } from '@/constants/query-keys';
import type { AifiLevel } from '@/domains/pages/ai-proficiency/constants';
import type { LevelMigrationSummary } from '@/domains/pages/ai-proficiency/fixtures/migration';
import { AIFI_PROFICIENCY_MIGRATION_VARIABLES, GROUP_BY_AIFI_PROFICIENCY } from '@/domains/pages/ai-proficiency/utils/aifi-aggregates-api';
import { buildOrgScoreTrendChartConfig, resolveAiProficiencyAggregateTimeRange } from '@/domains/pages/ai-proficiency/utils/api-payloads';
import { mapLevelMixFromChartData, mapLevelSnapshots, mapOrgScoreSnapshot, mapOrgScoreTimeFromChartData, type LevelSnapshot, type OrgScoreSnapshot } from '@/domains/pages/ai-proficiency/utils/map-aggregates';
import { mapProficiencyMigrationFromAggregates } from '@/domains/pages/ai-proficiency/utils/map-migration';
import { mapSegmentLevelShareMatrix, mapSegmentScoresFromChartData, mapSegmentSummaries, mapSegmentSummaryToScoreSnapshot, type SegmentSummary } from '@/domains/pages/ai-proficiency/utils/map-segment-aggregates';
import { buildSegmentFilterV2Values, buildSegmentGroupBy, buildSegmentLevelMixChartConfig, buildSegmentScoreTrendChartConfig, type SegmentKind } from '@/domains/pages/ai-proficiency/utils/segment-api-payloads';
import { computeBestLevelBenchmarks, computeRankAtBucket, computeSegmentRank, computeSegmentScoreStats, computeWorstBestEnvelope, inferWorstBestEnvelope, type SegmentScoreStats, type WorstBestEnvelopePoint } from '@/domains/pages/ai-proficiency/utils/segment-comparisons';
import { getAifiTimeRangeShortLabel } from '@/domains/pages/ai-proficiency/constants';
import { formatTrendBucketLabel } from '@/domains/pages/shared/trend-query';
import { AI_PROFICIENCY_PAGE_QUERY_OPTIONS as rollingChartQueryOptions, buildAiProficiencyChartTrendQueryKey, resolveAiProficiencyMonthlyTrendQuery, resolveWeeklyFallbackTrendQuery, shouldFallbackToWeeklyRollingTrend } from '@/domains/pages/ai-proficiency/utils/rolling-chart-trend';
import { trimLeadingEmptyChartTrendData } from '@/domains/pages/ai-proficiency/utils/trim-chart-trend-buckets';
import { alignSeriesToBuckets } from '@/domains/pages/shared/chart-axis';
import type { LevelBenchmarkShare } from '@/domains/pages/ai-proficiency/fixtures/benchmarks';
import { mapAnalyticsConfiguration } from '@/domains/reports/utils/configuration-mapper';
import { AI_PROFICIENCY_PAGE_QUERY_OPTIONS, aiProficiencyLadderProficiencyQueryKey, aiProficiencyOrgScoreQueryKey, aiProficiencySegmentsQueryKey, getAiProficiencyTimeRangeQueryKey } from '@/domains/pages/ai-proficiency/query-options';
import { isQueryCacheMissing } from '@/domains/pages/ai-proficiency/utils/query-loading';
import { useCallback, useMemo } from 'react';
import { useAiProficiencyEffectiveNow, useAiProficiencyStableNow } from '@/domains/pages/ai-proficiency/hooks/use-ai-proficiency-stable-now';
import { useUserDimensionMapping } from '@/domains/pages/shared/use-user-dimension-mapping';
import { applySegmentDisplayNames, getSegmentDisplayName } from '@/domains/pages/ai-proficiency/utils/segment-display';
const EMPTY_BUCKETS: string[] = [];
export type SegmentLevelBenchmark = LevelBenchmarkShare & {
  bestSegmentName: string;
};
export type AiProficiencySegmentPageData = {
  segments: SegmentSummary[];
  selectedSegmentName: string;
  segmentScore: OrgScoreSnapshot;
  orgScore: OrgScoreSnapshot;
  segmentScoreStats: SegmentScoreStats;
  segmentRank: number;
  levels: LevelSnapshot[];
  orgLevels: LevelSnapshot[];
  levelBenchmarks: Record<AifiLevel, SegmentLevelBenchmark>;
  trendLabels: string[];
  orgScoresOverTime: Array<number | null>;
  segmentScoresOverTime: Array<number | null>;
  otherSegmentScoresOverTime: Record<string, Array<number | null>>;
  worstBestEnvelope: WorstBestEnvelopePoint[];
  levelMixOverTime: Record<string, number[]>;
  levelCountsOverTime: Record<string, number[]>;
  migration: LevelMigrationSummary;
  migrationTimeRangeLabel: string;
  isLoading: boolean;
  isInitialLoading: boolean;
  isChartsLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
  computeTrendRank: (bucketIndex: number) => number;
  formatSegmentName: (name: string) => string;
};
type UseAiProficiencySegmentPageDataParams = {
  projectId: string | undefined;
  segmentKind: SegmentKind;
  selectedSegmentName: string | undefined;
};
export function useAiProficiencySegmentPageData({
  projectId,
  segmentKind,
  selectedSegmentName
}: UseAiProficiencySegmentPageDataParams): AiProficiencySegmentPageData {
  const stableNow = useAiProficiencyStableNow();
  const {
    effectiveNow,
    isReady: isEffectiveNowReady
  } = useAiProficiencyEffectiveNow();
  const stableNowKey = stableNow.getTime();
  const effectiveNowKey = effectiveNow.getTime();
  const aggregateTimeRange = useMemo(() => resolveAiProficiencyAggregateTimeRange(stableNow, effectiveNow), [stableNowKey, effectiveNowKey, stableNow, effectiveNow]);
  const aggregateTimeRangeQueryKey = useMemo(() => getAiProficiencyTimeRangeQueryKey(aggregateTimeRange), [aggregateTimeRange]);
  const monthlyTrendQuery = useMemo(() => resolveAiProficiencyMonthlyTrendQuery(stableNow, effectiveNow), [stableNowKey, effectiveNowKey]);
  const enabled = Boolean(projectId && aggregateTimeRange && isEffectiveNowReady);
  const {
    hasMapping,
    isLoading: isLoadingMapping
  } = useUserDimensionMapping(segmentKind, enabled);
  const monthlyChartTrendQueryKey = useMemo(() => buildAiProficiencyChartTrendQueryKey(monthlyTrendQuery), [monthlyTrendQuery]);
  const orgTrendMonthlyPayload = useMemo(() => mapAnalyticsConfiguration(buildOrgScoreTrendChartConfig(monthlyTrendQuery)), [monthlyTrendQuery]);
  const segmentTrendMonthlyPayload = useMemo(() => mapAnalyticsConfiguration(buildSegmentScoreTrendChartConfig(monthlyTrendQuery, segmentKind)), [monthlyTrendQuery, segmentKind]);
  const segmentsQuery = useQuery({
    queryKey: aiProficiencySegmentsQueryKey(segmentKind, projectId!, aggregateTimeRangeQueryKey),
    queryFn: ({
      signal
    }) => getInteractionAggregates({
      project_id: projectId!,
      time_range: aggregateTimeRange!,
      filters: [],
      variables: ['aifi_score', 'n_users'],
      group_by: buildSegmentGroupBy(segmentKind),
      calculate_previous: true,
      limit: 100
    }, {
      signal
    }),
    enabled,
    ...AI_PROFICIENCY_PAGE_QUERY_OPTIONS
  });
  const segments = useMemo(() => {
    const raw = segmentsQuery.data ? mapSegmentSummaries(segmentsQuery.data) : [];
    return applySegmentDisplayNames(raw, hasMapping, segmentKind);
  }, [hasMapping, segmentKind, segmentsQuery.data]);
  const formatSegmentName = useCallback((name: string) => getSegmentDisplayName(name, segments, hasMapping, segmentKind), [hasMapping, segmentKind, segments]);
  const resolvedSelectedName = useMemo(() => {
    if (selectedSegmentName && segments.some(segment => segment.name === selectedSegmentName)) {
      return selectedSegmentName;
    }
    return segments[0]?.name ?? '';
  }, [segments, selectedSegmentName]);
  const segmentFilterValues = useMemo(() => resolvedSelectedName ? [resolvedSelectedName] : [], [resolvedSelectedName]);
  const segmentFilter = useMemo(() => segmentFilterValues.length > 0 ? buildSegmentFilterV2Values(segmentKind, segmentFilterValues) : null, [segmentFilterValues, segmentKind]);
  const segmentReady = enabled && segmentsQuery.isSuccess && Boolean(resolvedSelectedName);
  const orgScoreQuery = useQuery({
    queryKey: aiProficiencyOrgScoreQueryKey(projectId!, aggregateTimeRangeQueryKey),
    queryFn: ({
      signal: signal_0
    }) => getInteractionAggregates({
      project_id: projectId!,
      time_range: aggregateTimeRange!,
      filters: [],
      variables: ['aifi_score'],
      calculate_previous: true
    }, {
      signal: signal_0
    }),
    enabled,
    ...AI_PROFICIENCY_PAGE_QUERY_OPTIONS
  });
  const ladderOrgProficiencyQuery = useQuery({
    queryKey: aiProficiencyLadderProficiencyQueryKey(projectId!, aggregateTimeRangeQueryKey),
    queryFn: ({
      signal: signal_1
    }) => getInteractionAggregates({
      project_id: projectId!,
      time_range: aggregateTimeRange!,
      filters: [],
      variables: ['n_users'],
      group_by: GROUP_BY_AIFI_PROFICIENCY,
      limit: 100
    }, {
      signal: signal_1
    }),
    enabled: segmentReady,
    ...AI_PROFICIENCY_PAGE_QUERY_OPTIONS
  });
  const ladderAllSegmentsQuery = useQuery({
    queryKey: [QUERY_KEY_INTERACTION_AGGREGATES, 'ai-proficiency-segment-ladder-all', segmentKind, projectId, aggregateTimeRangeQueryKey],
    queryFn: ({
      signal: signal_2
    }) => getInteractionAggregates({
      project_id: projectId!,
      time_range: aggregateTimeRange!,
      filters: [],
      variables: ['n_users'],
      group_by: GROUP_BY_AIFI_PROFICIENCY,
      additional_group_bys: [buildSegmentGroupBy(segmentKind)],
      limit: 500
    }, {
      signal: signal_2
    }),
    enabled: segmentReady,
    ...AI_PROFICIENCY_PAGE_QUERY_OPTIONS
  });
  const ladderSelectedSegmentQuery = useQuery({
    queryKey: [QUERY_KEY_INTERACTION_AGGREGATES, 'ai-proficiency-segment-ladder-selected', segmentKind, resolvedSelectedName, projectId, aggregateTimeRangeQueryKey],
    queryFn: ({
      signal: signal_3
    }) => getInteractionAggregates({
      project_id: projectId!,
      time_range: aggregateTimeRange!,
      filters: [],
      filters_v2: [segmentFilter!],
      variables: ['n_users'],
      group_by: GROUP_BY_AIFI_PROFICIENCY,
      limit: 100
    }, {
      signal: signal_3
    }),
    enabled: segmentReady && Boolean(segmentFilter),
    ...AI_PROFICIENCY_PAGE_QUERY_OPTIONS
  });
  const proficiencyMigrationQuery = useQuery({
    queryKey: [QUERY_KEY_INTERACTION_AGGREGATES, 'ai-proficiency-segment-migration', segmentKind, resolvedSelectedName, projectId, aggregateTimeRangeQueryKey],
    queryFn: ({
      signal: signal_4
    }) => getInteractionAggregates({
      project_id: projectId!,
      time_range: aggregateTimeRange!,
      filters: [],
      filters_v2: [segmentFilter!],
      variables: AIFI_PROFICIENCY_MIGRATION_VARIABLES,
      calculate_previous: false,
      limit: 1
    }, {
      signal: signal_4
    }),
    enabled: segmentReady,
    ...AI_PROFICIENCY_PAGE_QUERY_OPTIONS
  });
  const orgTrendMonthlyQuery = useQuery({
    queryKey: [QUERY_KEY_FETCH_ANALYTICS_CHART_DATA, 'ai-proficiency-segment-org-trend', projectId, monthlyChartTrendQueryKey],
    queryFn: ({
      signal: signal_5
    }) => getChartData(projectId!, orgTrendMonthlyPayload, {
      signal: signal_5
    }),
    enabled: segmentReady,
    ...rollingChartQueryOptions
  });
  const segmentTrendMonthlyQuery = useQuery({
    queryKey: [QUERY_KEY_FETCH_ANALYTICS_CHART_DATA, 'ai-proficiency-segment-trends', segmentKind, projectId, monthlyChartTrendQueryKey],
    queryFn: ({
      signal: signal_6
    }) => getChartData(projectId!, segmentTrendMonthlyPayload, {
      signal: signal_6
    }),
    enabled: segmentReady,
    ...rollingChartQueryOptions
  });
  const needsWeeklyFallback = useMemo(() => shouldFallbackToWeeklyRollingTrend(orgTrendMonthlyQuery.data?.chart, orgTrendMonthlyQuery.isSuccess), [orgTrendMonthlyQuery.data?.chart, orgTrendMonthlyQuery.isSuccess]);
  const weeklyTrendQuery = useMemo(() => resolveWeeklyFallbackTrendQuery(orgTrendMonthlyQuery.data?.chart, monthlyTrendQuery), [monthlyTrendQuery, orgTrendMonthlyQuery.data?.chart]);
  const weeklyChartTrendQueryKey = useMemo(() => buildAiProficiencyChartTrendQueryKey(weeklyTrendQuery), [weeklyTrendQuery]);
  const orgTrendWeeklyPayload = useMemo(() => mapAnalyticsConfiguration(buildOrgScoreTrendChartConfig(weeklyTrendQuery)), [weeklyTrendQuery]);
  const segmentTrendWeeklyPayload = useMemo(() => mapAnalyticsConfiguration(buildSegmentScoreTrendChartConfig(weeklyTrendQuery, segmentKind)), [segmentKind, weeklyTrendQuery]);
  const orgTrendWeeklyQuery = useQuery({
    queryKey: [QUERY_KEY_FETCH_ANALYTICS_CHART_DATA, 'ai-proficiency-segment-org-trend', projectId, weeklyChartTrendQueryKey],
    queryFn: ({
      signal: signal_7
    }) => getChartData(projectId!, orgTrendWeeklyPayload, {
      signal: signal_7
    }),
    enabled: segmentReady && needsWeeklyFallback,
    ...rollingChartQueryOptions
  });
  const segmentTrendWeeklyQuery = useQuery({
    queryKey: [QUERY_KEY_FETCH_ANALYTICS_CHART_DATA, 'ai-proficiency-segment-trends', segmentKind, projectId, weeklyChartTrendQueryKey],
    queryFn: ({
      signal: signal_8
    }) => getChartData(projectId!, segmentTrendWeeklyPayload, {
      signal: signal_8
    }),
    enabled: segmentReady && needsWeeklyFallback,
    ...rollingChartQueryOptions
  });
  const levelMixMonthlyPayload = useMemo(() => resolvedSelectedName ? mapAnalyticsConfiguration(buildSegmentLevelMixChartConfig(monthlyTrendQuery, segmentKind, segmentFilterValues)) : null, [monthlyTrendQuery, resolvedSelectedName, segmentFilterValues, segmentKind]);
  const levelMixWeeklyPayload = useMemo(() => resolvedSelectedName ? mapAnalyticsConfiguration(buildSegmentLevelMixChartConfig(weeklyTrendQuery, segmentKind, segmentFilterValues)) : null, [resolvedSelectedName, segmentFilterValues, segmentKind, weeklyTrendQuery]);
  const levelMixMonthlyQuery = useQuery({
    queryKey: [QUERY_KEY_FETCH_ANALYTICS_CHART_DATA, 'ai-proficiency-segment-level-mix', segmentKind, resolvedSelectedName, projectId, monthlyChartTrendQueryKey],
    queryFn: ({
      signal: signal_9
    }) => getChartData(projectId!, levelMixMonthlyPayload!, {
      signal: signal_9
    }),
    enabled: segmentReady && Boolean(levelMixMonthlyPayload),
    ...rollingChartQueryOptions
  });
  const levelMixWeeklyQuery = useQuery({
    queryKey: [QUERY_KEY_FETCH_ANALYTICS_CHART_DATA, 'ai-proficiency-segment-level-mix', segmentKind, resolvedSelectedName, projectId, weeklyChartTrendQueryKey],
    queryFn: ({
      signal: signal_10
    }) => getChartData(projectId!, levelMixWeeklyPayload!, {
      signal: signal_10
    }),
    enabled: segmentReady && needsWeeklyFallback && Boolean(levelMixWeeklyPayload),
    ...rollingChartQueryOptions
  });
  const activeTrendQuery = needsWeeklyFallback ? weeklyTrendQuery : monthlyTrendQuery;
  const orgScore = useMemo(() => orgScoreQuery.data ? mapOrgScoreSnapshot(orgScoreQuery.data) : null, [orgScoreQuery.data]);
  const segmentScore = useMemo(() => {
    const selected = segments.find(segment_0 => segment_0.name === resolvedSelectedName);
    return mapSegmentSummaryToScoreSnapshot(selected);
  }, [resolvedSelectedName, segments]);
  const segmentScoreStats = useMemo(() => computeSegmentScoreStats(segments), [segments]);
  const segmentRank = useMemo(() => computeSegmentRank(segments, resolvedSelectedName), [resolvedSelectedName, segments]);
  const levels = useMemo(() => {
    if (!ladderSelectedSegmentQuery.data) return [];
    return mapLevelSnapshots(ladderSelectedSegmentQuery.data);
  }, [ladderSelectedSegmentQuery.data]);
  const orgLevels = useMemo(() => {
    if (!ladderOrgProficiencyQuery.data || !orgScore) return [];
    return mapLevelSnapshots(ladderOrgProficiencyQuery.data);
  }, [ladderOrgProficiencyQuery.data, orgScore]);
  const levelBenchmarks = useMemo(() => {
    if (!ladderAllSegmentsQuery.data) {
      return {} as Record<AifiLevel, SegmentLevelBenchmark>;
    }
    const shareMatrix = mapSegmentLevelShareMatrix(ladderAllSegmentsQuery.data);
    const benchmarks = computeBestLevelBenchmarks(shareMatrix);
    if (!hasMapping) return benchmarks;
    return Object.fromEntries(Object.entries(benchmarks).map(([level, benchmark]) => [level, {
      ...benchmark,
      bestSegmentName: formatSegmentName(benchmark.bestSegmentName)
    }])) as Record<AifiLevel, SegmentLevelBenchmark>;
  }, [formatSegmentName, hasMapping, ladderAllSegmentsQuery.data]);
  const orgTrendFromChart = useMemo(() => {
    const rawChart = needsWeeklyFallback ? orgTrendWeeklyQuery.data?.chart : orgTrendMonthlyQuery.data?.chart;
    if (!rawChart) return null;
    return mapOrgScoreTimeFromChartData(rawChart);
  }, [needsWeeklyFallback, orgTrendMonthlyQuery.data?.chart, orgTrendWeeklyQuery.data?.chart]);
  const segmentTrendFromChart = useMemo(() => {
    const rawChart_0 = needsWeeklyFallback ? segmentTrendWeeklyQuery.data?.chart : segmentTrendMonthlyQuery.data?.chart;
    if (!rawChart_0) return null;
    return mapSegmentScoresFromChartData(rawChart_0);
  }, [needsWeeklyFallback, segmentTrendMonthlyQuery.data?.chart, segmentTrendWeeklyQuery.data?.chart]);
  const levelMixFromChart = useMemo(() => {
    const rawChart_1 = needsWeeklyFallback ? levelMixWeeklyQuery.data?.chart : levelMixMonthlyQuery.data?.chart;
    if (!rawChart_1) return null;
    return mapLevelMixFromChartData(rawChart_1);
  }, [levelMixMonthlyQuery.data?.chart, levelMixWeeklyQuery.data?.chart, needsWeeklyFallback]);
  const orgBuckets = orgTrendFromChart?.buckets ?? EMPTY_BUCKETS;
  const segmentBuckets = segmentTrendFromChart?.buckets ?? EMPTY_BUCKETS;
  const canonicalBuckets = orgBuckets.length > 0 ? orgBuckets : segmentBuckets;
  const alignedSegmentScoresByName = useMemo(() => {
    const rawScores = segmentTrendFromChart?.scoresBySegment ?? {};
    const aligned: Record<string, Array<number | null>> = {};
    for (const [name_0, scores] of Object.entries(rawScores)) {
      aligned[name_0] = alignSeriesToBuckets(canonicalBuckets, segmentBuckets, scores);
    }
    return aligned;
  }, [canonicalBuckets, segmentBuckets, segmentTrendFromChart?.scoresBySegment]);
  const trimmedChartTrend = useMemo(() => {
    const alignedOrgScores = alignSeriesToBuckets(canonicalBuckets, orgBuckets, orgTrendFromChart?.scores ?? []);
    if (canonicalBuckets.length === 0) {
      return null;
    }
    if (!levelMixFromChart) {
      return {
        startIndex: 0,
        buckets: canonicalBuckets,
        mix: {},
        counts: {},
        orgScores: alignedOrgScores,
        segmentScoresByName: alignedSegmentScoresByName
      };
    }
    const levelMixBuckets = levelMixFromChart.buckets;
    const alignedMix = Object.fromEntries(Object.entries(levelMixFromChart.mix).map(([level_0, values]) => [level_0, alignSeriesToBuckets(canonicalBuckets, levelMixBuckets, values).map(value => value ?? 0)]));
    const alignedCounts = Object.fromEntries(Object.entries(levelMixFromChart.counts).map(([level_1, values_0]) => [level_1, alignSeriesToBuckets(canonicalBuckets, levelMixBuckets, values_0).map(value_0 => value_0 ?? 0)]));
    return trimLeadingEmptyChartTrendData({
      buckets: canonicalBuckets,
      mix: alignedMix,
      counts: alignedCounts,
      orgScores: alignedOrgScores,
      segmentScoresByName: alignedSegmentScoresByName
    });
  }, [alignedSegmentScoresByName, canonicalBuckets, levelMixFromChart, orgBuckets, orgTrendFromChart?.scores]);
  const trendBuckets = trimmedChartTrend?.buckets ?? canonicalBuckets;
  const trendLabels = useMemo(() => trendBuckets.map((bucket, index) => formatTrendBucketLabel(bucket, activeTrendQuery.granularity, activeTrendQuery.bucketKind, index === trendBuckets.length - 1)), [activeTrendQuery.bucketKind, activeTrendQuery.granularity, trendBuckets]);
  const orgScoresOverTime = trimmedChartTrend?.orgScores ?? [];
  const segmentScoresByName = trimmedChartTrend?.segmentScoresByName ?? alignedSegmentScoresByName;
  const segmentScoresOverTime = useMemo(() => segmentScoresByName[resolvedSelectedName] ?? [], [resolvedSelectedName, segmentScoresByName]);
  const otherSegmentScoresOverTime = useMemo(() => {
    const result: Record<string, Array<number | null>> = {};
    for (const [name_1, scores_0] of Object.entries(segmentScoresByName)) {
      if (name_1 !== resolvedSelectedName) {
        result[name_1] = scores_0;
      }
    }
    return result;
  }, [segmentScoresByName, resolvedSelectedName]);
  const worstBestEnvelope = useMemo(() => inferWorstBestEnvelope(computeWorstBestEnvelope(segmentScoresByName, trendBuckets.length)), [segmentScoresByName, trendBuckets.length]);
  const levelMixOverTime = trimmedChartTrend?.mix ?? {};
  const levelCountsOverTime = trimmedChartTrend?.counts ?? {};
  const migration = useMemo(() => {
    if (!proficiencyMigrationQuery.isSuccess || !proficiencyMigrationQuery.data) {
      return {
        movedUp: 0,
        slippedBack: 0,
        stayed: 0
      };
    }
    return mapProficiencyMigrationFromAggregates(proficiencyMigrationQuery.data);
  }, [proficiencyMigrationQuery.isSuccess, proficiencyMigrationQuery.data]);
  const pickerScoresByName = useMemo(() => {
    const aligned_0: Record<string, Array<number | null>> = {};
    for (const segment_1 of segments) {
      aligned_0[segment_1.name] = segmentScoresByName[segment_1.name] ?? [];
    }
    return aligned_0;
  }, [segments, segmentScoresByName]);
  const computeTrendRank = useCallback((bucketIndex: number) => computeRankAtBucket(pickerScoresByName, resolvedSelectedName, bucketIndex), [pickerScoresByName, resolvedSelectedName]);
  const segmentChartsPending = segmentReady && (isQueryCacheMissing(ladderOrgProficiencyQuery) || isQueryCacheMissing(ladderAllSegmentsQuery) || isQueryCacheMissing(ladderSelectedSegmentQuery) || isQueryCacheMissing(orgTrendMonthlyQuery) || isQueryCacheMissing(segmentTrendMonthlyQuery) || isQueryCacheMissing(levelMixMonthlyQuery) || needsWeeklyFallback && (isQueryCacheMissing(orgTrendWeeklyQuery) || isQueryCacheMissing(segmentTrendWeeklyQuery) || isQueryCacheMissing(levelMixWeeklyQuery)) || isQueryCacheMissing(proficiencyMigrationQuery));
  const isInitialLoading = !isEffectiveNowReady || enabled && (isQueryCacheMissing(segmentsQuery) || isQueryCacheMissing(orgScoreQuery) || isLoadingMapping);
  const isChartsLoading = segmentChartsPending && Boolean(segmentsQuery.data);
  const isLoading = isInitialLoading;
  const isError = segmentsQuery.isError || orgScoreQuery.isError || ladderOrgProficiencyQuery.isError || ladderAllSegmentsQuery.isError || ladderSelectedSegmentQuery.isError || orgTrendMonthlyQuery.isError || segmentTrendMonthlyQuery.isError || levelMixMonthlyQuery.isError || needsWeeklyFallback && (orgTrendWeeklyQuery.isError || segmentTrendWeeklyQuery.isError || levelMixWeeklyQuery.isError);
  const error = segmentsQuery.error ?? orgScoreQuery.error ?? ladderOrgProficiencyQuery.error ?? ladderAllSegmentsQuery.error ?? ladderSelectedSegmentQuery.error ?? orgTrendMonthlyQuery.error ?? segmentTrendMonthlyQuery.error ?? levelMixMonthlyQuery.error ?? (needsWeeklyFallback ? orgTrendWeeklyQuery.error ?? segmentTrendWeeklyQuery.error ?? levelMixWeeklyQuery.error : null);
  const refetch = useCallback(() => {
    void segmentsQuery.refetch();
    void orgScoreQuery.refetch();
    void ladderOrgProficiencyQuery.refetch();
    void ladderAllSegmentsQuery.refetch();
    void ladderSelectedSegmentQuery.refetch();
    void proficiencyMigrationQuery.refetch();
    void orgTrendMonthlyQuery.refetch();
    void segmentTrendMonthlyQuery.refetch();
    void levelMixMonthlyQuery.refetch();
    if (needsWeeklyFallback) {
      void orgTrendWeeklyQuery.refetch();
      void segmentTrendWeeklyQuery.refetch();
      void levelMixWeeklyQuery.refetch();
    }
  }, [ladderAllSegmentsQuery.refetch, ladderOrgProficiencyQuery.refetch, ladderSelectedSegmentQuery.refetch, levelMixMonthlyQuery.refetch, levelMixWeeklyQuery.refetch, needsWeeklyFallback, orgScoreQuery.refetch, orgTrendMonthlyQuery.refetch, orgTrendWeeklyQuery.refetch, proficiencyMigrationQuery.refetch, segmentTrendMonthlyQuery.refetch, segmentTrendWeeklyQuery.refetch, segmentsQuery.refetch]);
  return {
    segments,
    selectedSegmentName: resolvedSelectedName,
    segmentScore: segmentScore ?? {
      score: null,
      scorePrev: null,
      delta: null,
      totalUsers: 0
    },
    orgScore: orgScore ?? {
      score: null,
      scorePrev: null,
      delta: null,
      totalUsers: 0
    },
    segmentScoreStats,
    segmentRank,
    levels,
    orgLevels,
    levelBenchmarks,
    trendLabels,
    orgScoresOverTime,
    segmentScoresOverTime,
    otherSegmentScoresOverTime,
    worstBestEnvelope,
    levelMixOverTime,
    levelCountsOverTime,
    migration,
    migrationTimeRangeLabel: getAifiTimeRangeShortLabel(TimeRangeKind['30_days']),
    isLoading,
    isInitialLoading,
    isChartsLoading,
    isError,
    error,
    refetch,
    computeTrendRank,
    formatSegmentName
  };
}